import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { LinkTo } from '@ember/routing';
import { on } from '@ember/modifier';
import { service } from '@ember/service';
import Chip from 'tio-reimagined/components/chip';
import { PhCaretDown } from 'ember-phosphor-icons';
import ApplicationRow from './application-row';
import {
  APPLICATION_STATUS_MAP,
  PROGRAM_INSTANCE_STATUS_MAP,
  DEFAULT_STATUS,
  type ChipIntent,
} from 'tio-employee/utils/tas/applicationStateMap';
import type { IntlService } from 'ember-intl';
import type { ProgramInstance } from 'tio-common/utils/tuition-assistance/application-states';

export type { ProgramInstance };

interface ProgramInstanceCardSignature {
  Args: {
    instance: ProgramInstance;
    isExpandable?: boolean; // If true, allows collapse/expand (default: true)
    defaultExpanded?: boolean; // Initial expanded state (default: false)
    onAction?: () => void; // Optional action handler
    actionText?: string; // Optional action button text
  };
  Element: HTMLDivElement;
}

export default class ProgramInstanceCard extends Component<ProgramInstanceCardSignature> {
  @service declare intl: IntlService;

  @tracked isExpanded = this.args.defaultExpanded ?? false;

  get isExpandable() {
    return this.args.isExpandable ?? true;
  }

  get showAction() {
    return this.args.onAction !== undefined;
  }

  toggleExpanded = () => {
    if (this.isExpandable) {
      this.isExpanded = !this.isExpanded;
    }
  };

  handleAction = () => {
    if (this.args.onAction) {
      this.args.onAction();
    }
  };

  get instanceApp() {
    return this.args.instance.instanceApplication;
  }

  get instanceId() {
    return this.instanceApp.tasProgramInstanceIdKeyword || this.instanceApp.id;
  }

  get statusInfo(): { label: string; intent: ChipIntent } {
    const app = this.instanceApp;

    // If there's a refund amount due, show "In Repayment" regardless of state
    if (app.paymentRefundRequestedAmount && app.paymentRefundRequestedAmount > 0) {
      return { label: 'In Repayment', intent: 'danger' };
    }

    // For program instances (Type 4), check tasProgramInstanceStateKeyword first
    // Then fall back to tasApplicationStateKeyword for child applications
    const state = app.tasProgramInstanceStateKeyword || app.tasApplicationStateKeyword || '';
    const mapped =
      PROGRAM_INSTANCE_STATUS_MAP[state] || APPLICATION_STATUS_MAP[state] || DEFAULT_STATUS;
    return { label: mapped.label, intent: mapped.intent };
  }

  get instanceName() {
    const app = this.instanceApp;

    // Priority: tasProgramInstanceFieldProgramSpecializationKeyword > tasProgramInstanceFieldSchoolInstitutionNameKeyword > formatted date
    return (
      app.tasProgramInstanceFieldProgramSpecializationKeyword ||
      app.tasProgramInstanceFieldSchoolInstitutionNameKeyword ||
      this.formatDateAsName(app.tasProgramInstanceCreatedAtDate || app.createdAt)
    );
  }

  formatDateAsName(dateStr?: string): string {
    if (!dateStr) return 'Program Instance';

    const date = new Date(dateStr);
    return this.intl.formatDate(date, {
      month: 'long',
      year: 'numeric',
    });
  }

  get templateName() {
    return this.instanceApp.tasProgramTemplateFieldProgramNameKeyword || 'Unknown Program';
  }

  get formattedCreatedDate() {
    const dateStr = this.instanceApp.tasProgramInstanceCreatedAtDate;
    if (!dateStr) return '';

    const date = new Date(dateStr);
    const formatted = this.intl.formatDate(date, {
      month: '2-digit',
      day: '2-digit',
      year: '2-digit',
    });
    return `created ${formatted}`;
  }

  get formattedUpdatedDate() {
    const dateStr = this.instanceApp.updatedAt;
    if (!dateStr) return '';

    const date = new Date(dateStr);
    const formatted = this.intl.formatDate(date, {
      month: '2-digit',
      day: '2-digit',
      year: '2-digit',
    });
    return `updated ${formatted}`;
  }

  get showStatusChip() {
    // Only show chip on instance header if there are no child applications
    // Otherwise, chips will show on the individual applications
    return this.args.instance.childApplications.length === 0;
  }

  <template>
    <div class="border tio-border-default rounded-xl overflow-hidden" ...attributes>
      {{! Header Row - Expandable or Static based on @isExpandable }}
      {{#if this.isExpandable}}
        {{! Expandable header with caret icon }}
        <button
          type="button"
          class="w-full p-4 tio-row-loose hover:tio-bg-sunken transition-colors"
          {{on "click" this.toggleExpanded}}
        >
          {{! Instance info }}
          <div class="flex-1 min-w-0 flex flex-col gap-1.5 text-left">
            {{! Row 1: Instance Name (linked) + Status Chip (optional) }}
            <div class="tio-row-tight">
              <LinkTo
                @route="authenticated.tas.programs.show"
                @model={{this.instanceId}}
                class="tio-copy"
              >
                {{this.instanceName}}
              </LinkTo>
              {{#if this.showStatusChip}}
                <Chip @intent={{this.statusInfo.intent}} @appearance="faded" @size="sm">
                  {{this.statusInfo.label}}
                </Chip>
              {{/if}}
            </div>

            {{! Row 2: Template name • created date • updated date }}
            <div class="flex items-center gap-1.5 text-sm tio-fg-disabled tracking-wide">
              <span>{{this.templateName}}</span>
              <span class="text-gray-300">•</span>
              <span>{{this.formattedCreatedDate}}</span>
              {{#if this.formattedUpdatedDate}}
                <span class="text-gray-300">•</span>
                <span>{{this.formattedUpdatedDate}}</span>
              {{/if}}
            </div>
          </div>

          {{! Caret icon }}
          <PhCaretDown
            class="tio-fg-disabled transition-transform {{if this.isExpanded 'rotate-180'}}"
          />
        </button>
      {{else}}
        {{! Static header with optional action }}
        <div class="w-full p-4 tio-row-loose">
          {{! Instance info }}
          <div class="flex-1 min-w-0 flex flex-col gap-1.5">
            {{! Row 1: Instance Name (linked) + Status Chip (optional) }}
            <div class="tio-row-tight">
              <LinkTo
                @route="authenticated.tas.programs.show"
                @model={{this.instanceId}}
                class="font-medium tio-fg-primary hover:text-ocean-600 hover:underline"
              >
                {{this.instanceName}}
              </LinkTo>
              {{#if this.showStatusChip}}
                <Chip @intent={{this.statusInfo.intent}} @appearance="faded" @size="sm">
                  {{this.statusInfo.label}}
                </Chip>
              {{/if}}
            </div>

            {{! Row 2: Template name • created date • updated date }}
            <div class="flex items-center gap-1.5 text-sm tio-fg-disabled tracking-wide">
              <span>{{this.templateName}}</span>
              <span class="text-gray-300">•</span>
              <span>{{this.formattedCreatedDate}}</span>
              {{#if this.formattedUpdatedDate}}
                <span class="text-gray-300">•</span>
                <span>{{this.formattedUpdatedDate}}</span>
              {{/if}}
            </div>
          </div>

          {{! Optional action button/text }}
          {{#if this.showAction}}
            <button
              type="button"
              class="tio-copy text-sm font-semibold"
              {{on "click" this.handleAction}}
            >
              {{@actionText}}
            </button>
          {{/if}}
        </div>
      {{/if}}

      {{! Child Applications - Conditional based on expandable/expanded state }}
      {{#if @instance.childApplications.length}}
        {{#if this.isExpandable}}
          {{#if this.isExpanded}}
            <div class="divide-y divide-gray-200">
              {{#each @instance.childApplications as |application|}}
                <ApplicationRow @application={{application}} @isNested={{true}} />
              {{/each}}
            </div>
          {{/if}}
        {{else}}
          <div class="divide-y divide-gray-200">
            {{#each @instance.childApplications as |application|}}
              <ApplicationRow @application={{application}} @isNested={{true}} />
            {{/each}}
          </div>
        {{/if}}
      {{/if}}
    </div>
  </template>
}
